import React, { useMemo, useState, useEffect } from 'react';
import { formatCurrency, formatPercentage } from '../utils/formatters';

const COLORS = [
  '#3B82F6', // blue-500
  '#22C55E', // green-500
  '#EAB308', // yellow-500
  '#A855F7', // purple-500
  '#EC4899', // pink-500
  '#14B8A6', // teal-500
  '#F97316', // orange-500
  '#6366F1', // indigo-500
  '#84CC16', // lime-500
];

const DonutChart = ({
  data = [],
  size = 180,
  thickness = 35,
  showLabels = true,
  showLegend = true,
  showTotal = true,
  totalLabel = 'Total',
  valueFormatter = formatCurrency,
  centerLabel,
  centerValue,
  onSegmentClick,
  animationKey,
  className = '',
}) => {
  const chartData = useMemo(() => {
    const total = data.reduce((sum, item) => sum + (item.value || 0), 0);
    if (total === 0) return { segments: [], total: 0 };

    const radiusForLength = (size - thickness) / 2;
    let currentAngle = -90; // Start from top
    const segments = data
      .filter((item) => item.value > 0)
      .map((item, index) => {
        const percentage = (item.value / total) * 100;
        const angle = (item.value / total) * 360;
        const angleRad = (angle * Math.PI) / 180;
        const pathLength = angleRad * radiusForLength;
        const segment = {
          ...item,
          percentage,
          startAngle: currentAngle,
          endAngle: currentAngle + angle,
          pathLength,
          color: item.color || COLORS[index % COLORS.length],
        };
        currentAngle += angle;
        return segment;
      });

    return { segments, total };
  }, [data, size, thickness]);

  const center = size / 2;
  const radius = (size - thickness) / 2;

  const [mounted, setMounted] = useState(false);
  const drawDuration = 0.4;
  useEffect(() => {
    setMounted(false);
    const id = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(id);
  }, [data, animationKey]);

  // Convert angle to coordinates
  const polarToCartesian = (angle) => {
    const radians = (angle * Math.PI) / 180;
    return {
      x: center + radius * Math.cos(radians),
      y: center + radius * Math.sin(radians),
    };
  };

  // Create arc path - handles full circle case
  const createArc = (startAngle, endAngle) => {
    const angleDiff = endAngle - startAngle;
    
    // For a full or near-full circle, draw two half-circles
    if (angleDiff >= 359.9) {
      const mid = polarToCartesian(startAngle + 180);
      const start = polarToCartesian(startAngle);
      return [
        'M', start.x, start.y,
        'A', radius, radius, 0, 1, 1, mid.x, mid.y,
        'A', radius, radius, 0, 1, 1, start.x, start.y,
      ].join(' ');
    }
    
    const start = polarToCartesian(startAngle);
    const end = polarToCartesian(endAngle);
    const largeArcFlag = angleDiff > 180 ? 1 : 0;

    return [
      'M', start.x, start.y,
      'A', radius, radius, 0, largeArcFlag, 1, end.x, end.y,
    ].join(' ');
  };

  if (chartData.total === 0) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
        <p className="text-sm text-gray-400 dark:text-gray-500">No data</p>
      </div>
    );
  }

  return (
    <div className={`flex flex-col sm:flex-row items-center gap-4 ${className}`}>
      {/* SVG Donut */}
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          {/* Background circle */}
          <circle
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={thickness}
            className="text-gray-200 dark:text-gray-700"
          />
          {/* Segments - native <title> for tooltips (Tippy breaks SVG when used inside <svg>) */}
          {chartData.segments.map((segment, index) => (
            <g
              key={segment.filterValue ?? (segment.name || index)}
              className={onSegmentClick ? 'cursor-pointer' : ''}
              onClick={onSegmentClick ? () => onSegmentClick(segment) : undefined}
              role={onSegmentClick ? 'button' : undefined}
            >
              {segment.tooltip && <title>{segment.tooltip}</title>}
              <path
                d={createArc(segment.startAngle, segment.endAngle)}
                fill="none"
                stroke={segment.color}
                strokeWidth={thickness}
                strokeLinecap="butt"
                strokeDasharray={segment.pathLength}
                strokeDashoffset={mounted ? 0 : segment.pathLength}
                style={{ transition: mounted ? `stroke-dashoffset ${drawDuration}s ease-out` : 'none' }}
                className="transition-all duration-300"
              />
            </g>
          ))}
        </svg>
        {/* Center label */}
        {showLabels && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {centerLabel != null ? centerLabel : totalLabel}
            </span>
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {centerValue != null ? centerValue : valueFormatter(chartData.total)}
            </span>
          </div>
        )}
      </div>

      {/* Legend */}
      {showLegend && (
        <div className="flex flex-col gap-1.5 min-w-0">
          {chartData.segments.map((segment, index) => (
            <div key={segment.filterValue ?? (segment.name || index)} className="flex items-center gap-2 text-sm">
              <span
                className="w-3 h-3 rounded-sm flex-shrink-0"
                style={{ backgroundColor: segment.color }}
              />
              <span className="text-gray-700 dark:text-gray-300 truncate">
                {segment.name}
              </span>
              <span className="text-gray-500 dark:text-gray-400 ml-auto tabular-nums">
                {formatPercentage(segment.percentage)}
              </span>
              {segment.displayValue !== undefined && (
                <span className="text-gray-600 dark:text-gray-300 font-medium tabular-nums">
                  ({valueFormatter(segment.displayValue ?? segment.value)})
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DonutChart;
